/**
 * Componentes de interface compartilhados.
 *
 * Cobrem explicitamente os estados exigidos pelo briefing: CARREGANDO, VAZIO e
 * FALHA — cada um com mensagem acionável, não apenas um spinner.
 */

import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { artigoDaRota } from '../help/porRota';
import { useBeforeUnload } from '../hooks/useUnsavedChanges';

// ==================== ícones ================================================
// SVG inline: sem dependência de biblioteca de ícones e sem requisição extra.

type IconProps = { className?: string };

const icon = (path: ReactNode) =>
  function Icon({ className = 'h-4 w-4' }: IconProps) {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        {path}
      </svg>
    );
  };

export const IconDashboard = icon(
  <>
    <rect x="2.5" y="2.5" width="6" height="6" rx="1" />
    <rect x="11.5" y="2.5" width="6" height="4" rx="1" />
    <rect x="2.5" y="11.5" width="6" height="6" rx="1" />
    <rect x="11.5" y="9.5" width="6" height="8" rx="1" />
  </>,
);
export const IconBuilding = icon(
  <>
    <path d="M3.5 17.5v-13a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v13" />
    <path d="M12.5 8.5h3a1 1 0 0 1 1 1v8" />
    <path d="M2 17.5h16M6 6.5h1.5M9 6.5h1.5M6 9.5h1.5M9 9.5h1.5M6 12.5h1.5M9 12.5h1.5" />
  </>,
);
export const IconUsers = icon(
  <>
    <circle cx="7.5" cy="7" r="2.8" />
    <path d="M2.5 16.5c0-2.5 2.2-4.2 5-4.2s5 1.7 5 4.2" />
    <path d="M13.5 5.2a2.6 2.6 0 0 1 0 5" />
    <path d="M14.5 12.6c1.9.4 3 1.8 3 3.9" />
  </>,
);
export const IconTarget = icon(
  <>
    <circle cx="10" cy="10" r="7" />
    <circle cx="10" cy="10" r="3.2" />
    <path d="M10 3v2M10 15v2M3 10h2M15 10h2" />
  </>,
);
export const IconKanban = icon(
  <>
    <rect x="2.5" y="2.5" width="4" height="12" rx="1" />
    <rect x="8" y="2.5" width="4" height="8" rx="1" />
    <rect x="13.5" y="2.5" width="4" height="15" rx="1" />
  </>,
);
export const IconCalendar = icon(
  <>
    <rect x="2.5" y="4" width="15" height="13.5" rx="1.5" />
    <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" />
  </>,
);
export const IconHandshake = icon(
  <>
    <path d="M2.5 8.5 6 5h3l1.5 1.5L9 8l1.5 1.5" />
    <path d="M17.5 8.5 14 5h-3" />
    <path d="M3 9v4.5l4 3 3-2 3 2 4-3V9" />
  </>,
);
export const IconShield = icon(
  <>
    <path d="M10 2.5 4 4.8v4.7c0 3.5 2.4 6.6 6 7.9 3.6-1.3 6-4.4 6-7.9V4.8L10 2.5Z" />
    <path d="m7.6 9.8 1.8 1.8 3.2-3.4" />
  </>,
);
export const IconSettings = icon(
  <>
    <circle cx="10" cy="10" r="2.6" />
    <path d="M10 2.5v2M10 15.5v2M3.9 6.1l1.5 1.5M14.6 12.4l1.5 1.5M2.5 10h2M15.5 10h2M3.9 13.9l1.5-1.5M14.6 7.6l1.5-1.5" />
  </>,
);
export const IconSearch = icon(
  <>
    <circle cx="8.8" cy="8.8" r="5.3" />
    <path d="m12.8 12.8 4 4" />
  </>,
);
export const IconPlus = icon(<path d="M10 4v12M4 10h12" />);
export const IconCheck = icon(<path d="m4 10.5 4 4 8-9" />);
/** Etiqueta de preço — Catálogo Comercial (CERES-004). */
export const IconTag = icon(
  <>
    <path d="M10.6 2.8 17 9.2a1.5 1.5 0 0 1 0 2.1l-5.7 5.7a1.5 1.5 0 0 1-2.1 0L2.8 10.6A1.5 1.5 0 0 1 2.4 9.5l.3-5.2a1.5 1.5 0 0 1 1.6-1.6l5.2-.3c.4 0 .8.1 1.1.4Z" />
    <circle cx="7" cy="7" r="1.2" />
  </>,
);
export const IconAlert = icon(
  <>
    <path d="M10 3.2 2.6 16.2h14.8L10 3.2Z" />
    <path d="M10 8v3.2M10 13.6h.01" />
  </>,
);
export const IconClock = icon(
  <>
    <circle cx="10" cy="10" r="7" />
    <path d="M10 6v4.3l3 1.8" />
  </>,
);
export const IconChevronRight = icon(<path d="m8 5 5 5-5 5" />);
export const IconChevronLeft = icon(<path d="m12 5-5 5 5 5" />);
export const IconChevronDown = icon(<path d="m5 8 5 5 5-5" />);
export const IconClose = icon(<path d="m5 5 10 10M15 5 5 15" />);
export const IconMenu = icon(<path d="M3 5.5h14M3 10h14M3 14.5h14" />);
export const IconChart = icon(<path d="M3.5 16.5h13M6 13.5v-4M10 13.5v-8M14 13.5v-6" />);
export const IconTrend = icon(
  <>
    <path d="m3 14 4.5-4.5 3 3L17 6" />
    <path d="M12.5 6H17v4.5" />
  </>,
);
export const IconBriefcase = icon(
  <>
    <rect x="2.5" y="6" width="15" height="10.5" rx="1.5" />
    <path d="M7 6V4.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V6M2.5 10.5h15" />
  </>,
);
export const IconInbox = icon(
  <>
    <path d="M3 11.5 5 4.5h10l2 7v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4Z" />
    <path d="M3 11.5h4l1 2h4l1-2h4" />
  </>,
);
export const IconLogout = icon(
  <>
    <path d="M7.5 17.5H4a1.5 1.5 0 0 1-1.5-1.5V4A1.5 1.5 0 0 1 4 2.5h3.5" />
    <path d="M13 6.5 16.5 10 13 13.5M7 10h9.5" />
  </>,
);
export const IconNote = icon(
  <>
    <path d="M4 3.5h9l3.5 3.5v9.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1Z" />
    <path d="M12.5 3.5V7h3.5M6 10.5h7M6 13.5h5" />
  </>,
);
export const IconSpark = icon(
  <>
    <path d="M10 2.5 11.6 7l4.9 1.6-4.9 1.6L10 15l-1.6-4.8L3.5 8.6 8.4 7 10 2.5Z" />
  </>,
);
export const IconPencil = icon(
  <>
    <path d="M4 16h3.5l8-8a1.8 1.8 0 0 0-2.5-2.5l-8 8V16Z" />
    <path d="m12.5 5.5 2 2" />
  </>,
);
export const IconTrash = icon(
  <>
    <path d="M4 6.5h12M8 6.5V4.5h4v2M6 6.5l.7 9.2a1 1 0 0 0 1 .8h4.6a1 1 0 0 0 1-.8L14 6.5" />
  </>,
);

// ==================== estados de tela =======================================

export function Spinner({ className = 'h-4 w-4' }: IconProps): JSX.Element {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" aria-hidden="true">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z"
      />
    </svg>
  );
}

/** Estado de CARREGAMENTO: esqueleto com a forma do conteúdo esperado. */
export function LoadingRows({
  rows = 5,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}): JSX.Element {
  return (
    <div className="space-y-2 p-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Carregando…</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <div
              key={columnIndex}
              className="skeleton h-5"
              style={{
                width: columnIndex === 0 ? '32%' : `${Math.max(12, 22 - columnIndex * 2)}%`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Estado VAZIO: explica o que fazer, não apenas informa a ausência. */
export function EmptyState({
  title,
  description,
  action,
  icon: Icon = IconSearch,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: (props: IconProps) => JSX.Element;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-3 rounded-full bg-surface-sunken p-3 text-ink-subtle">
        <Icon className="h-6 w-6" />
      </div>
      <p className="text-base font-medium text-ink">{title}</p>
      {description !== undefined && (
        <p className="mt-1 max-w-md text-sm text-ink-muted">{description}</p>
      )}
      {action !== undefined && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Estado de FALHA: mensagem acionável + identificador para suporte. */
export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}): JSX.Element {
  const apiError = error instanceof ApiError ? error : null;
  const message =
    apiError?.message ??
    (error instanceof ApiError
      ? error.mensagemHumana
      : error instanceof Error
        ? error.message
        : 'Não foi possível carregar os dados.');

  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-3 rounded-full bg-danger-50 p-3 text-danger-600">
        <IconAlert className="h-6 w-6" />
      </div>
      <p className="text-base font-medium text-ink">Não foi possível carregar</p>
      <p className="mt-1 max-w-md text-sm text-ink-muted">{message}</p>
      {apiError !== null && (
        <p className="mt-2 font-mono text-2xs text-ink-subtle">
          requisição {apiError.correlationId}
        </p>
      )}
      {onRetry !== undefined && (
        <button type="button" className="btn-secondary btn-sm mt-4" onClick={onRetry}>
          Tentar novamente
        </button>
      )}
    </div>
  );
}

/** Alerta em linha para erro de formulário. */
export function InlineError({ error }: { error: unknown }): JSX.Element | null {
  if (error === null || error === undefined) return null;
  const apiError = error instanceof ApiError ? error : null;
  const message =
    apiError?.message ??
    (error instanceof ApiError
      ? error.mensagemHumana
      : error instanceof Error
        ? error.message
        : 'Ocorreu um erro.');

  // Pendências de transição de etapa vêm em `missingLabels`.
  const details = apiError?.details as { missingLabels?: string[] } | undefined;

  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700"
    >
      <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0">
        <p className="font-medium">{message}</p>
        {details?.missingLabels !== undefined && details.missingLabels.length > 0 && (
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            {details.missingLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        )}
        {apiError !== null && apiError.status >= 500 && (
          <p className="mt-1 font-mono text-2xs opacity-80">requisição {apiError.correlationId}</p>
        )}
      </div>
    </div>
  );
}

// ==================== avisos (toast) ========================================

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  message: string;
  acao?: AcaoDoAviso;
}

/**
 * A AÇÃO DE DESFAZER DENTRO DO AVISO (CERES-032).
 *
 * Existe para operações que a pessoa pode ter disparado sem querer e que ainda
 * dá para reverter com segurança — arquivar, mandar para a lixeira. O lugar do
 * "Desfazer" é o próprio aviso que conta o que aconteceu: procurar a pesquisa
 * na aba certa para desfazer é a versão difícil do mesmo gesto.
 *
 * Opcional de propósito: aviso sem volta atrás não ganha um botão que promete
 * uma que não existe.
 */
export interface AcaoDoAviso {
  rotulo: string;
  onSelect: () => void;
}

interface ToastContextValue {
  notify: (kind: Toast['kind'], message: string, acao?: AcaoDoAviso) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const timers = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number): void => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    const activeTimers = timers.current;
    return () => {
      for (const timer of activeTimers.values()) window.clearTimeout(timer);
      activeTimers.clear();
    };
  }, []);

  const notify = useCallback((kind: Toast['kind'], message: string, acao?: AcaoDoAviso) => {
    counter.current += 1;
    const id = counter.current;
    setToasts((current) => [
      ...current,
      { id, kind, message, ...(acao === undefined ? {} : { acao }) },
    ]);
    // Some sozinho: feedback imediato não deve exigir ação do usuário. O aviso
    // com "Desfazer" fica mais tempo — quatro segundos e meio é pouco para ler,
    // decidir que foi engano e agir.
    const timer = window.setTimeout(() => {
      timers.current.delete(id);
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, acao === undefined ? 4500 : 9000);
    timers.current.set(id, timer);
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
        role="status"
        aria-live="polite"
        data-testid="toast-region"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-pop ${
              toast.kind === 'success'
                ? 'border-positive-200 bg-positive-50 text-positive-700'
                : toast.kind === 'error'
                  ? 'border-danger-200 bg-danger-50 text-danger-700'
                  : 'border-info-200 bg-info-50 text-info-700'
            }`}
          >
            {toast.kind === 'success' ? (
              <IconCheck className="mt-0.5 h-4 w-4 shrink-0" />
            ) : toast.kind === 'error' ? (
              <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <IconSpark className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span className="min-w-0 flex-1">{toast.message}</span>
            {toast.acao !== undefined && (
              <button
                type="button"
                className="shrink-0 font-semibold underline underline-offset-2"
                data-testid="toast-acao"
                onClick={() => {
                  dismiss(toast.id);
                  toast.acao?.onSelect();
                }}
              >
                {toast.acao.rotulo}
              </button>
            )}
            <button
              type="button"
              aria-label="Fechar aviso"
              className="shrink-0 opacity-60 hover:opacity-100"
              onClick={() => dismiss(toast.id)}
            >
              <IconClose className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === null) {
    throw new Error('useToast precisa estar dentro de ToastProvider.');
  }
  return context;
}

// ==================== diálogo modal =========================================

/**
 * Modal acessível: fecha com Escape, devolve o foco ao fechar e prende o foco
 * dentro do diálogo (navegação por teclado nas ações essenciais).
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dirty = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /**
   * O rodapé do diálogo.
   *
   * Aceita uma FUNÇÃO que recebe `{ fechar }` — o mesmo fechamento que o
   * clique fora, o Escape e o botão "Fechar" usam. Um "Cancelar" que chamasse
   * `onClose` direto seria uma QUARTA saída, e a única sem a pergunta: o
   * formulário evaporaria por ela enquanto os outros três caminhos
   * perguntavam. Foi exatamente assim que a BKC-008 nasceu, um caminho de cada
   * vez.
   */
  footer?: ReactNode | ((acoes: { fechar: () => void }) => ReactNode);
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Há alteração não salva? (BKC-008)
   *
   * Quando verdadeiro, fechar — por clique fora, Escape ou botão — pede
   * confirmação. Perder um formulário longo por um clique fora foi registrado no
   * ciclo de teste, e não é problema de conveniência: é o que faz a pessoa
   * preferir anotar no papel antes de digitar.
   *
   * O padrão é `false` de propósito: um modal que só exibe informação não deve
   * pedir confirmação para fechar.
   */
  dirty?: boolean;
}): JSX.Element | null {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);

  /**
   * Fecha, ou pergunta antes.
   *
   * Centralizado porque há TRÊS caminhos de saída (clique fora, Escape, botão) e
   * cada um deles precisa da mesma decisão. Enquanto isso vivia em cada `onClick`,
   * bastava esquecer um para o formulário evaporar por aquele caminho.
   */
  const tentarFechar = useCallback((): void => {
    if (dirty) {
      setConfirmandoDescarte(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  const descartar = useCallback((): void => {
    setConfirmandoDescarte(false);
    onClose();
  }, [onClose]);

  // Reabrir o modal não deve cair na confirmação pendente da vez anterior.
  useEffect(() => {
    if (!open) setConfirmandoDescarte(false);
  }, [open]);

  // Fechar a aba com formulário preenchido: o navegador só aceita o diálogo dele.
  useBeforeUnload(open && dirty);

  /**
   * O fechamento visto pelo efeito, sem entrar nas dependências dele.
   *
   * `onClose` chega como arrow inline em praticamente toda tela — função nova a
   * cada render. Enquanto ela era dependência do efeito abaixo, o efeito rodava
   * A CADA TECLA: devolvia o foco para o começo do diálogo e a barra de espaço
   * seguinte, agora sobre o botão "Fechar", fechava o modal e apagava o
   * formulário. O sintoma para quem usa era "o sistema apagou o que eu estava
   * preenchendo".
   */
  const fecharRef = useRef(tentarFechar);
  useEffect(() => {
    fecharRef.current = tentarFechar;
  }, [tentarFechar]);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    /**
     * O foco vai para o primeiro CAMPO — e o botão de fechar não conta.
     *
     * `querySelector('input, select, textarea, button')` devolve o primeiro
     * elemento em ordem de DOCUMENTO, e o "Fechar" mora no cabeçalho: ele vinha
     * antes de qualquer campo, ao contrário do que o código dizia fazer. Quando
     * não há campo nenhum (um modal só de confirmação), o primeiro botão do
     * corpo é a escolha certa.
     */
    const corpo = panel?.querySelector<HTMLElement>('[data-modal-corpo]') ?? panel;
    const alvo =
      corpo?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
      ) ?? corpo?.querySelector<HTMLElement>('button:not([disabled]), a[href]');
    alvo?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        // Escape com formulário preenchido pede confirmação, como o clique fora.
        // Antes, era a saída mais fácil de acionar sem intenção.
        fecharRef.current();
        return;
      }
      if (event.key !== 'Tab' || panel === null) return;

      const focusable = [
        ...panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
    // Só `open`: ver o comentário do `fecharRef`. Qualquer outra dependência aqui
    // volta a reposicionar o foco no meio da digitação.
  }, [open]);

  if (!open) return null;

  const width = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }[size];

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto p-4 sm:items-center">
      <div className="fixed inset-0 bg-surface-overlay" onClick={tentarFechar} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative z-10 my-auto w-full ${width} rounded-lg border border-line bg-surface shadow-pop`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold text-ink">
              {title}
            </h2>
            {description !== undefined && (
              <p className="mt-0.5 text-sm text-ink-muted">{description}</p>
            )}
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm -mr-1 -mt-1 shrink-0"
            onClick={tentarFechar}
            aria-label="Fechar"
          >
            <IconClose />
          </button>
        </div>
        <div
          data-modal-corpo=""
          className="max-h-[calc(100vh-16rem)] overflow-y-auto scroll-slim px-4 py-4"
        >
          {children}
        </div>
        {footer !== undefined && (
          <div className="flex items-center justify-end gap-2 border-t border-line bg-surface-sunken px-4 py-3">
            {typeof footer === 'function' ? footer({ fechar: tentarFechar }) : footer}
          </div>
        )}

        {/* Confirmação DENTRO do diálogo, e não um `window.confirm`: o nativo não
            é estilizável, não é testável de forma confiável e sai do fluxo visual
            do sistema. Fica por cima para deixar claro que a decisão é sobre o
            que está atrás. */}
        {confirmandoDescarte && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-surface-overlay p-4"
            role="alertdialog"
            aria-label="Descartar alterações?"
            data-testid="descartar-confirmacao"
          >
            <div className="w-full max-w-sm rounded-lg border border-line bg-surface p-4">
              <p className="text-base font-semibold text-ink">Descartar o que você preencheu?</p>
              <p className="mt-1 text-sm text-ink-muted">
                As alterações deste formulário não foram salvas e serão perdidas.
              </p>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  data-testid="descartar-cancelar"
                  onClick={() => setConfirmandoDescarte(false)}
                  autoFocus
                >
                  Continuar editando
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  data-testid="descartar-confirmar"
                  onClick={descartar}
                >
                  Descartar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Confirmação para ação de impacto relevante. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-base text-ink-muted">{message}</p>
    </Modal>
  );
}

/**
 * PAINEL LATERAL (CERES-032).
 *
 * O lugar do conteúdo inteiro quando a tela precisa mostrar só um resumo. A
 * matriz de comparação nasceu deste problema: despejar o texto completo em
 * cada célula tornava a tabela ilegível, e escondê-lo tornava a tabela inútil.
 * O painel resolve os dois — a célula mostra duas linhas, o painel mostra tudo,
 * e a matriz continua atrás, no lugar onde a pessoa estava.
 *
 * Por que NÃO é um `Modal`: modal interrompe e pede decisão; painel acompanha.
 * Quem abre uma evidência quer continuar comparando, e não responder a uma
 * pergunta. Ainda assim ele tem o mesmo cuidado de teclado — `role="dialog"`,
 * Escape fecha, o foco entra ao abrir e volta para quem abriu ao fechar —
 * porque quem navega por teclado não deve ficar preso atrás dele.
 */
export function PainelLateral({
  aberto,
  titulo,
  subtitulo,
  onFechar,
  children,
  testId,
}: {
  aberto: boolean;
  titulo: string;
  subtitulo?: string;
  onFechar: () => void;
  children: ReactNode;
  testId?: string;
}): JSX.Element | null {
  const painel = useRef<HTMLDivElement>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);
  const tituloId = useId();

  useEffect(() => {
    if (!aberto) return undefined;
    focoAnterior.current = document.activeElement as HTMLElement | null;
    painel.current?.focus();
    const tecla = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onFechar();
      }
    };
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('keydown', tecla);
      focoAnterior.current?.focus();
    };
  }, [aberto, onFechar]);

  if (!aberto) return null;

  return (
    <>
      {/*
       * O fundo é clicável para fechar, mas NÃO escurece a tela como um modal:
       * a comparação atrás continua legível, que é justamente o ponto de ser um
       * painel e não um diálogo.
       */}
      <div className="fixed inset-0 z-[70] bg-ink/10" onClick={onFechar} aria-hidden="true" />
      {/*
       * A LARGURA É FIXA, E POR QUÊ (CERES-033 §3.5).
       *
       * `max-w-lg` dava 512px em telas médias — largo o bastante para o painel
       * competir com a matriz por espaço em 1280×720. 28rem (448px) é a faixa
       * que a especificação pede e cabe ao lado de três colunas de comparação
       * sem apertá-las. O painel é `fixed`, então ele passa POR CIMA e não
       * recalcula a largura das colunas: a matriz atrás continua do tamanho
       * que estava, que é o que faltava acontecer.
       *
       * `z-[70]` fica acima da camada de tela cheia da comparação (`z-[60]`):
       * o painel abre lá dentro e precisa cobri-la.
       */}
      <div
        ref={painel}
        role="dialog"
        aria-modal="false"
        aria-labelledby={tituloId}
        tabIndex={-1}
        data-testid={testId}
        className="fixed inset-y-0 right-0 z-[80] flex w-full flex-col overflow-y-auto border-l border-line bg-surface shadow-pop outline-none sm:w-[28rem]"
      >
        <header className="sticky top-0 flex items-start justify-between gap-3 border-b border-line bg-surface px-5 py-4">
          <div className="min-w-0">
            <h2 id={tituloId} className="text-base font-semibold text-ink">
              {titulo}
            </h2>
            {subtitulo !== undefined && (
              <p className="mt-0.5 text-xs text-ink-muted">{subtitulo}</p>
            )}
          </div>
          <button
            type="button"
            className="btn-ghost h-8 w-8 shrink-0 justify-center p-0"
            aria-label="Fechar painel"
            onClick={onFechar}
          >
            <IconClose className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 space-y-4 px-5 py-4">{children}</div>
      </div>
    </>
  );
}

// ==================== primitivos de formulário ==============================

/**
 * Rotulo + controle + mensagem de erro.
 *
 * Quando `error` esta presente, o controle filho recebe `aria-invalid` e
 * `aria-describedby` apontando para a mensagem — sem isso um leitor de tela
 * anuncia o campo como valido e a pessoa nao descobre o que corrigir. A
 * marcacao e feita por clonagem porque os controles sao passados como
 * `children`: exigir que cada chamada repita os atributos garantiria que alguem
 * esquecesse.
 */
export function Field({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  children,
  className = '',
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const generatedId = useId();
  const errorId = `${htmlFor ?? generatedId}-erro`;

  return (
    <div className={className}>
      <label className="field-label" htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="ml-0.5 text-danger-600" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {/* Marca o controle como invalido e o liga a mensagem. Feito por clonagem
          para que nenhuma chamada precise repetir os atributos — repeticao
          garantiria esquecimento. */}
      {error !== undefined && isValidElement(children)
        ? cloneElement(children as ReactElement<Record<string, unknown>>, {
            'aria-invalid': true,
            'aria-describedby': errorId,
          })
        : children}
      {error !== undefined ? (
        <p className="field-error" id={errorId}>
          <IconAlert className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : (
        hint !== undefined && <p className="field-hint">{hint}</p>
      )}
    </div>
  );
}

/**
 * Resumo acessivel dos erros de validacao de um formulario.
 *
 * Um erro por campo resolve para quem ve a tela inteira; quem navega por teclado
 * ou leitor de tela precisa de uma lista no comeco, com link para cada campo. Os
 * itens usam `<a href="#id">` de proposito: e a forma nativa de mover o foco.
 */
export function FormErrorSummary({
  error,
  title = 'Corrija os campos indicados',
}: {
  error: unknown;
  title?: string;
}): JSX.Element | null {
  if (!(error instanceof ApiError)) return null;
  const issues = error.fieldIssueSummary;
  if (issues.length === 0) return null;

  return (
    <div
      role="alert"
      tabIndex={-1}
      className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700"
      data-testid="form-error-summary"
    >
      <p className="flex items-center gap-2 font-medium">
        <IconAlert className="h-4 w-4 shrink-0" />
        {title}
      </p>
      <ul className="mt-1 list-inside list-disc space-y-0.5">
        {issues.map((issue) => (
          <li key={`${issue.field}-${issue.message}`}>
            <a href={`#${issue.field}`} className="underline hover:no-underline">
              {issue.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Seção de formulário.
 *
 * O briefing proíbe "telas cheias de campos sem hierarquia": os formulários são
 * divididos por CONTEXTO, com título e descrição curtos.
 */
export function FormSection({
  title,
  description,
  children,
  columns = 2,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  columns?: 1 | 2 | 3;
}): JSX.Element {
  const grid = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' }[columns];
  return (
    <section className="border-b border-line pb-5 last:border-b-0 last:pb-0">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {description !== undefined && (
          <p className="mt-0.5 text-xs text-ink-muted">{description}</p>
        )}
      </div>
      <div className={`grid grid-cols-1 gap-x-4 gap-y-3 ${grid}`}>{children}</div>
    </section>
  );
}

/** Par rótulo/valor para telas de detalhe. */
export function DataItem({
  label,
  children,
  className = '',
}: {
  label: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={className}>
      <dt className="text-xs font-medium text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 text-base text-ink">{children}</dd>
    </div>
  );
}

/**
 * NAVEGAÇÃO INTERNA DE UMA FICHA (CERES-017).
 *
 * Existia em cinco telas, copiada à mão, cada uma com o seu `role="tablist"`.
 * Virou componente porque a ficha da empresa passou a depender dela para não
 * ser uma página de dois metros: o que era rolagem vira escolha.
 *
 * Acessibilidade não é opcional aqui — é o que separa "abas" de "botões que
 * mudam coisas": `tablist`/`tab`, seleção anunciada, e as setas do teclado
 * andando entre as abas como leitor de tela espera.
 *
 * Em tela estreita a faixa rola na horizontal em vez de quebrar em duas
 * linhas; a aba ativa continua alcançável.
 */
export interface AbaDaFicha {
  chave: string;
  rotulo: string;
  /** Contagem opcional ao lado do rótulo (ex.: número de contatos). */
  contagem?: number;
}

export function Tabs({
  abas,
  ativa,
  aoTrocar,
  rotuloDaNavegacao,
  idPrefixo = 'aba',
}: {
  abas: readonly AbaDaFicha[];
  ativa: string;
  aoTrocar: (chave: string) => void;
  /** Nome acessível da faixa — diz de QUE ficha são estas abas. */
  rotuloDaNavegacao: string;
  idPrefixo?: string;
}): JSX.Element {
  const indiceAtivo = Math.max(
    0,
    abas.findIndex((aba) => aba.chave === ativa),
  );

  const aoTeclar = (evento: React.KeyboardEvent<HTMLButtonElement>): void => {
    const passo = evento.key === 'ArrowRight' ? 1 : evento.key === 'ArrowLeft' ? -1 : 0;
    if (passo === 0) return;
    evento.preventDefault();
    const destino = abas[(indiceAtivo + passo + abas.length) % abas.length];
    if (destino !== undefined) aoTrocar(destino.chave);
  };

  return (
    <div
      className="scroll-slim mb-4 flex items-center gap-1 overflow-x-auto border-b border-line px-1 pb-2"
      role="tablist"
      aria-label={rotuloDaNavegacao}
    >
      {abas.map((aba) => {
        const selecionada = aba.chave === ativa;
        return (
          <button
            key={aba.chave}
            type="button"
            role="tab"
            aria-selected={selecionada}
            tabIndex={selecionada ? 0 : -1}
            data-testid={`${idPrefixo}-${aba.chave}`}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded px-2.5 py-1 text-sm font-medium transition ${
              selecionada
                ? 'bg-brand-50 text-brand-700'
                : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'
            }`}
            onClick={() => aoTrocar(aba.chave)}
            onKeyDown={aoTeclar}
          >
            {aba.rotulo}
            {aba.contagem !== undefined && aba.contagem > 0 && (
              <span className="text-2xs font-normal text-ink-subtle">{aba.contagem}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Indicador numérico do dashboard. */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  icon: Icon,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'positive' | 'warning' | 'danger' | 'brand';
  icon?: (props: IconProps) => JSX.Element;
}): JSX.Element {
  const toneClass = {
    neutral: 'text-ink',
    positive: 'text-positive-700',
    warning: 'text-warning-700',
    danger: 'text-danger-700',
    brand: 'text-brand-700',
  }[tone];

  return (
    <div className="panel px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</p>
        {Icon !== undefined && <Icon className="h-4 w-4 shrink-0 text-ink-subtle" />}
      </div>
      <p className={`mt-1.5 text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
      {hint !== undefined && <p className="mt-0.5 text-xs text-ink-subtle">{hint}</p>}
    </div>
  );
}

/** Cabeçalho de página com ações principais evidentes à direita. */
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumb,
  help,
  marca,
}: {
  title: string;
  /**
   * Subtítulo. Aceita nó, e não só texto: algumas telas resumem o estado do
   * registro ali ("aceita · R$ 40.000 · versão 2 enviada"), e forçar string
   * faria essas telas montarem o próprio cabeçalho — que é exatamente como o
   * espaçamento do título começa a variar de tela para tela.
   */
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  /** Link discreto de ajuda contextual, logo abaixo do subtítulo. */
  help?: ReactNode;
  /**
   * Marca do registro, à esquerda do título (CRM-VAL-007 §30).
   *
   * Existe para o logotipo da empresa. Um slot no cabeçalho do sistema, e não
   * um cabeçalho próprio na tela: é assim que o espaçamento e o tamanho do
   * título continuam iguais em todas as telas — que é justamente o que se
   * perde quando cada uma monta o seu.
   *
   * Discreto por desenho: a ficha da empresa é ferramenta de trabalho, não
   * painel de marketing.
   */
  marca?: ReactNode;
}): JSX.Element {
  /*
   * A AJUDA DA TELA APARECE SOZINHA (§11-F).
   *
   * A Central tinha trinta e sete artigos, e quem estava perdido precisava sair
   * da tela, abrir a Central, escolher a categoria e adivinhar o título — o que,
   * na prática, significava não ler nada. O cabeçalho passa a oferecer o artigo
   * certo da rota atual, e cada tela só precisa se preocupar com o que é seu.
   *
   * O `help` explícito continua valendo e tem precedência: há telas com ajuda
   * mais específica do que a do módulo inteiro.
   */
  const artigo = artigoDaRota(useLocation().pathname);

  return (
    <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        {marca !== undefined && <div className="mt-0.5 shrink-0">{marca}</div>}
        <div className="min-w-0">
          {breadcrumb !== undefined && <div className="mb-1">{breadcrumb}</div>}
          <h1 className="truncate text-2xl font-semibold tracking-tight text-ink">{title}</h1>
          {subtitle !== undefined && (
            <div className="mt-0.5 text-sm text-ink-muted">{subtitle}</div>
          )}
          {help !== undefined ? (
            <div className="mt-1">{help}</div>
          ) : (
            artigo !== null && (
              <p className="mt-1">
                <Link to={`/ajuda/${artigo}`} className="text-2xs text-ink-subtle hover:underline">
                  Como esta tela funciona
                </Link>
              </p>
            )
          )}
        </div>
      </div>
      {/* Em tela estreita o cabeçalho é coluna e as ações QUEBRAM de linha — sem
          isso os botões encolhiam abaixo do próprio texto e se sobrepunham. A
          partir de `sm`, `shrink-0` mantém a linha única de sempre. */}
      {actions !== undefined && (
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>
      )}
    </header>
  );
}

/** Paginação simples com total real. */
export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}): JSX.Element | null {
  const totalPages = total === 0 ? 0 : Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-sm text-ink-muted">
      <span className="tabular-nums">
        {from}–{to} de {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="btn-secondary btn-sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Anterior
        </button>
        <span className="px-2 tabular-nums">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          className="btn-secondary btn-sm"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          Próxima
        </button>
      </div>
    </div>
  );
}

/** Campo de busca com debounce, para não disparar uma requisição por tecla. */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Buscar…',
  className = '',
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}): JSX.Element {
  const [local, setLocal] = useState(value);
  const timer = useRef<number>();

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const handle = (next: string): void => {
    setLocal(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onChange(next), 300);
  };

  return (
    <div className={`relative ${className}`}>
      <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
      <input
        type="search"
        className="field-input pl-8"
        value={local}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        onChange={(event) => handle(event.target.value)}
      />
    </div>
  );
}

/** Reticências verticais: o gesto universal de "há mais ações aqui". */
export const IconDots = icon(
  <>
    <circle cx="10" cy="4.5" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="10" cy="10" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="10" cy="15.5" r="1.1" fill="currentColor" stroke="none" />
  </>,
);

/** Reticências horizontais: o "Mais" da navegação principal (CERES-UX-001). */
export const IconMais = icon(
  <>
    <circle cx="4.5" cy="10" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="10" cy="10" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="10" r="1.2" fill="currentColor" stroke="none" />
  </>,
);

/** Hoje: o dia com o trabalho marcado (CERES-UX-001). */
export const IconHoje = icon(
  <>
    <rect x="3" y="4" width="14" height="13" rx="1.5" />
    <path d="M3 8h14M7 2.5v3M13 2.5v3" />
    <path d="m7.5 12.5 2 2 3.5-4" />
  </>,
);

/** Conversas: balão de mensagem (CERES-UX-001). */
export const IconConversa = icon(
  <>
    <path d="M3.5 4.5h13v8.5H8l-4.5 3.5V4.5Z" />
    <path d="M7 8h6M7 10.5h4" />
  </>,
);

export interface AcaoDeMenu {
  /** Chave estável — usada como `key` e em `data-testid`. */
  chave: string;
  rotulo: string;
  onSelect: () => void;
  /** Vermelho e por último: exclusão e encerramento. */
  destrutiva?: boolean;
  desabilitada?: boolean;
  /** Explicação do porquê, quando desabilitada. Vira `title`. */
  motivo?: string;
  icone?: (props: IconProps) => JSX.Element;
}

/**
 * MENU DE AÇÕES CONTEXTUAL de uma linha de tabela (CRM-VAL-002 §11).
 *
 * Existe para resolver um problema de listagem: quatro botões por linha
 * transformam a tabela num painel de controle e escondem o dado, que é o que
 * a pessoa foi ali ver. O gesto do "⋮" é conhecido, ocupa uma coluna estreita
 * e mantém as ações a um clique.
 *
 * TRÊS DECISÕES DE ACESSIBILIDADE, e o que cada uma impede:
 *
 * 1. **`aria-haspopup` e `aria-expanded` no gatilho.** Sem eles, um leitor de
 *    tela anuncia "botão" e a pessoa não sabe que algo vai abrir — nem que já
 *    abriu.
 * 2. **Esc fecha e devolve o foco ao gatilho.** Um menu que fecha e larga o
 *    foco no corpo do documento faz quem navega por teclado recomeçar a tabela
 *    do início.
 * 3. **Clique fora fecha.** Sem isso o menu fica aberto por cima da linha
 *    seguinte, e o próximo clique da pessoa acerta o item errado.
 *
 * Ação DESABILITADA continua VISÍVEL, com o motivo no `title`. Esconder
 * "Excluir" de um item que não pode ser excluído faz a pessoa procurar o botão
 * que nunca vai encontrar; mostrá-lo apagado, com o porquê, responde a pergunta
 * antes dela.
 */
export function MenuDeAcoes({
  acoes,
  rotulo,
  testId,
}: {
  acoes: AcaoDeMenu[];
  /** Rótulo acessível do gatilho ("Abrir ações do item"). */
  rotulo: string;
  testId?: string;
}): JSX.Element | null {
  const [aberto, setAberto] = useState(false);
  const gatilho = useRef<HTMLButtonElement>(null);
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return undefined;

    const foraDaqui = (evento: MouseEvent): void => {
      const alvo = evento.target as Node;
      if (caixa.current?.contains(alvo) === true) return;
      if (gatilho.current?.contains(alvo) === true) return;
      setAberto(false);
    };
    const tecla = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return;
      setAberto(false);
      // Devolve o foco a quem abriu: sem isto, quem navega por teclado
      // recomeça a tabela do início.
      gatilho.current?.focus();
    };

    document.addEventListener('mousedown', foraDaqui);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', foraDaqui);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberto]);

  if (acoes.length === 0) return null;

  const ordenadas = [...acoes].sort(
    (a, b) => Number(a.destrutiva ?? false) - Number(b.destrutiva ?? false),
  );

  return (
    <div className="relative inline-block text-left">
      <button
        ref={gatilho}
        type="button"
        className="btn-ghost h-8 w-8 justify-center p-0"
        aria-label={rotulo}
        aria-haspopup="menu"
        aria-expanded={aberto}
        data-testid={testId}
        onClick={() => setAberto((atual) => !atual)}
      >
        <IconDots />
      </button>

      {aberto && (
        <div
          ref={caixa}
          role="menu"
          aria-label={rotulo}
          className="absolute right-0 z-20 mt-1 min-w-48 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-pop"
        >
          {ordenadas.map((acao) => (
            <button
              key={acao.chave}
              type="button"
              role="menuitem"
              data-testid={testId === undefined ? undefined : `${testId}-${acao.chave}`}
              disabled={acao.desabilitada}
              title={acao.motivo}
              className={
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ' +
                (acao.destrutiva === true
                  ? 'text-danger-700 hover:bg-danger-50'
                  : 'text-ink hover:bg-surface-sunken')
              }
              onClick={() => {
                setAberto(false);
                acao.onSelect();
              }}
            >
              {acao.icone !== undefined && <acao.icone className="h-4 w-4 shrink-0" />}
              {acao.rotulo}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
